import { TFunction } from 'i18next';
import { CampaignStateDefinition } from 'modules/graphql/types';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import {
  CampaignContributionsResultsData,
  CampaignContributionsRowType,
  CampaignContributionTableHeader,
} from 'modules/iterableGoals/types';
import { getTableColumns, printCampaignLaunchDate } from 'modules/iterableGoals/utils';
import { ColumnData } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { campaignStateBadge } from 'utils/badgeHelpers';
import { DATE_FORMAT_FULL_WITH_HOURS_MINS, displayDate } from 'utils/date';
import { formatAsPercent, localize } from 'utils/numberHelpers';
import { Cell, CellRight, LinkText } from './Styled';

type BodyPropType = {
  results: CampaignContributionsResultsData[];
};

const getTableBodyColumns = (tableColumns: ColumnData[], t: TFunction): Record<string, unknown>[] =>
  tableColumns.map((column) => {
    switch (column.value) {
      case CampaignContributionTableHeader.CAMPAIGN:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <Cell>
              <LinkText target="_blank" to={`/analytics/campaignPerformance?campaignId=${row.id}`}>
                {row[column.value as CampaignContributionTableHeader]}
              </LinkText>
            </Cell>
          ),
        };

      case CampaignContributionTableHeader.START_AT:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <Cell>
              {printCampaignLaunchDate(
                t,
                row.type,
                displayDate(
                  row[column.value as CampaignContributionTableHeader],
                  DATE_FORMAT_FULL_WITH_HOURS_MINS,
                ),
                false,
              )}
            </Cell>
          ),
        };

      case CampaignContributionTableHeader.STATUS:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <Cell>
              {campaignStateBadge(
                row[column.value as CampaignContributionTableHeader] as CampaignStateDefinition,
              )}
            </Cell>
          ),
        };

      case CampaignContributionTableHeader.SENDS:
      case CampaignContributionTableHeader.CONTRIBUTIONS:
      case CampaignContributionTableHeader.UNIQUE_CONTRIBUTIONS:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <CellRight>{localize(row[column.value as CampaignContributionTableHeader])}</CellRight>
          ),
        };

      case CampaignContributionTableHeader.DELIVERED:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <CellRight>
              {formatAsPercent(row[column.value as CampaignContributionTableHeader] as number)}
            </CellRight>
          ),
        };

      default:
        return {
          column,
          value: column.value,
          RowCell: ({ row }: CampaignContributionsRowType) => (
            <Cell>{row[column.value as CampaignContributionTableHeader]}</Cell>
          ),
        };
    }
  });

export const Body = ({ results }: BodyPropType) => {
  const { t } = useGoalsTranslations();
  const tableColumns = getTableColumns(t);

  return <TableRows idKey="name" rows={results} columns={getTableBodyColumns(tableColumns, t)} />;
};
